import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveEnd, Router } from '@angular/router';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { filter } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { AuthenticationService } from '../services/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class InstrumentationService {
  private readonly appInsights?: ApplicationInsights;

  constructor(
    router: Router,
    private authService: AuthenticationService,
  ) {
    // '-' is the "intentionally unset" sentinel from the variable group — treat
    // it as "no telemetry endpoint" so we don't 400 every track call.
    const apiKey = environment.appInsights.apiKey;
    if (apiKey && apiKey !== '-') {
      this.appInsights = new ApplicationInsights({
        config: { instrumentationKey: apiKey },
      });
      this.appInsights.loadAppInsights();

      this.addUserContextToAppInsights();

      router.events
        .pipe(filter((event): event is ResolveEnd => event instanceof ResolveEnd))
        .subscribe(event => {
          const activatedComponent = this.getActivatedComponent(event.state.root);
          if (activatedComponent) {
            this.logPageView(
              `${activatedComponent.name} ${this.getRouteTemplate(event.state.root)}`,
              event.urlAfterRedirects,
            );
          }
        });
    }
  }

  public logPageView(name: string, uri: string) {
    if (this.appInsights) {
      this.appInsights.trackPageView({
        name,
        uri,
        properties: this.addGlobalProperties(),
      });
    }
  }

  public logEvent(name: string) {
    if (this.appInsights) {
      this.appInsights.trackEvent({
        name,
        properties: this.addGlobalProperties(),
      });
    }
  }

  public logError(exception: Error) {
    if (this.appInsights) {
      this.appInsights.trackException({
        exception,
        properties: this.addGlobalProperties(),
      });
    }
  }

  private addUserContextToAppInsights() {
    if (this.appInsights) {
      if (this.authService.isAuthenticated()) {
        const userId = this.authService.getCurrentLoggedOnUserId();
        if (userId) {
          this.appInsights.setAuthenticatedUserContext(userId);
        }
      }
    }
  }

  private addGlobalProperties(properties?: { [key: string]: string }): { [key: string]: string } {
    if (!properties) {
      properties = {};
    }

    // add your custom properties such as app version

    return properties;
  }

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';
    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }
}
